/*
 * Store locator (sections/nadine-store-locator.liquid): the map, store list ⇄ detail, URLs and "open now".
 * Map: MapLibre GL from jsDelivr with OpenFreeMap's Positron style — free, no key, no cookies. It loads as soon as
 * the page is parsed; without WebGL the grey box stays and the panel works on its own.
 */
(() => {
  const root = document.querySelector('[data-nadine-locator]');
  if (!root) return;

  const MAPLIBRE = 'https://cdn.jsdelivr.net/npm/maplibre-gl@5.9.0/dist/maplibre-gl';
  const STYLE = 'https://tiles.openfreemap.org/styles/positron';
  const PIN =
    '<svg viewBox="0 0 30 40" aria-hidden="true"><path fill="currentColor" d="M15 0C6.7 0 0 6.6 0 14.8 0 25.9 15 40 15 40s15-14.1 15-25.2C30 6.6 23.3 0 15 0z"/><circle cx="15" cy="14.5" r="5" fill="#fff"/></svg>';

  const stores = JSON.parse(root.querySelector('[data-nadine-locator-data]').textContent);
  const i18n = JSON.parse(root.querySelector('[data-nadine-locator-i18n]').textContent);
  const listUrl = root.dataset.listUrl;
  const list = root.querySelector('[data-nadine-locator-list]');
  const details = new Map(
    [...root.querySelectorAll('[data-nadine-store-detail]')].map((el) => [el.dataset.nadineStoreDetail, el])
  );
  const mapBox = root.querySelector('[data-nadine-locator-map]');
  const mobile = window.matchMedia('(max-width: 749px)');

  let map = null;
  const markers = new Map();
  let current = [...details].find(([, el]) => !el.hidden)?.[0] || null;

  /* ---------- Open now (Milan time) ---------- */

  function romeNow() {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Rome',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date())
        .map((p) => [p.type, p.value])
    );
    const day = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(parts.weekday);
    return { day, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
  }

  const toMinutes = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  // "10:00-14:00,15:00-19:30;…" (Monday first) → [[[600, 840], [900, 1170]], …]
  const parseHours = (s) =>
    s.split(';').map((day) => (day ? day.split(',').map((span) => span.split('-').map(toMinutes)) : []));

  const fmt = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

  function statusFor(hours) {
    const { day, minutes } = romeNow();
    const today = hours[day] || [];
    const open = today.find(([from, to]) => minutes >= from && minutes < to);
    if (open) return { open: true, html: `<strong>${i18n.open}</strong> · ${i18n.closes.replace('__TIME__', fmt(open[1]))}` };
    const later = today.find(([from]) => from > minutes);
    if (later) return { open: false, html: `<strong>${i18n.closed}</strong> · ${i18n.opens.replace('__TIME__', fmt(later[0]))}` };
    const tomorrow = hours[(day + 1) % 7]?.[0];
    return {
      open: false,
      html: `<strong>${i18n.closed}</strong>${tomorrow ? ` · ${i18n.opensTomorrow.replace('__TIME__', fmt(tomorrow[0]))}` : ''}`,
    };
  }

  function renderStatus() {
    const { day } = romeNow();
    root.querySelectorAll('[data-nadine-store-status]').forEach((el) => {
      const status = statusFor(parseHours(el.dataset.nadineStoreStatus));
      el.innerHTML = status.html;
      el.classList.toggle('is-open', status.open);
    });
    root.querySelectorAll('.nadine-locator__hours tr').forEach((row) => {
      row.classList.toggle('is-today', Number(row.dataset.day) === day);
    });
  }

  renderStatus();
  setInterval(renderStatus, 60 * 1000);

  /* ---------- List ⇄ detail ---------- */

  // Opening a store is the "looked for the address" signal. Page-load views wait for the consent check
  // (window.nadinePosthogReady, snippets/posthog-init.liquid): opt-in/out empties the queue of unsent events.
  function trackView(store, via) {
    const send = () => window.posthog?.capture?.('store_viewed', { store, via });
    if (via === 'page' && window.nadinePosthogReady) window.nadinePosthogReady.then(send);
    else send();
  }

  // `via`: how the store was opened, for PostHog's store_viewed — list (a card), map (a pin), back (browser
  // back/forward), page (landed on a store page or #key: from Google, a link, a reload).
  function show(key, { push = false, focus = false, via = 'list' } = {}) {
    if (key && !details.has(key)) key = null;
    current = key;
    list.hidden = Boolean(key);
    details.forEach((el, k) => (el.hidden = k !== key));

    if (push) {
      const store = stores.find((s) => s.key === key);
      history.pushState({ nadineStore: key }, '', key ? store.url : listUrl);
    }

    const view = key ? details.get(key) : list;
    if (focus) {
      const heading = view.querySelector('.nadine-locator__title');
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      const panelTop = view.getBoundingClientRect().top + window.scrollY - 120;
      if (window.scrollY > panelTop || mobile.matches) window.scrollTo({ top: Math.max(panelTop, 0), behavior: 'smooth' });
    }

    if (key) trackView(key, via);
    updateMap();
  }

  root.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    const link = event.target.closest('[data-nadine-store-link], [data-nadine-store-back]');
    if (!link) return;
    event.preventDefault();
    show(link.dataset.nadineStoreLink || null, { push: true, focus: true });
  });

  window.addEventListener('popstate', (event) => {
    const key = event.state && 'nadineStore' in event.state ? event.state.nadineStore : initialKey;
    show(key, { via: 'back' });
  });

  // `#via-torino` on the list page opens that store (also handy while the store pages are unpublished).
  const hashKey = location.hash.slice(1);
  if (!current && details.has(hashKey)) show(hashKey, { via: 'page' });
  else if (current) trackView(current, 'page');
  const initialKey = current;
  history.replaceState({ ...(history.state || {}), nadineStore: current }, '');

  // Hovering a card lights its pin, and the other way round.
  root.querySelectorAll('.nadine-locator__card').forEach((card) => {
    card.addEventListener('mouseenter', () => markers.get(card.dataset.store)?.el.classList.add('is-hovered'));
    card.addEventListener('mouseleave', () => markers.get(card.dataset.store)?.el.classList.remove('is-hovered'));
  });

  /* ---------- Map ---------- */

  function padding() {
    return mobile.matches ? 48 : { top: 80, bottom: 80, left: 80, right: 80 };
  }

  function updateMap() {
    if (!map) return;
    markers.forEach(({ el }, key) => el.setAttribute('aria-current', String(key === current)));
    const store = stores.find((s) => s.key === current);
    if (store) {
      map.flyTo({ center: [store.lng, store.lat], zoom: 15.5, speed: 1.4, essential: true });
    } else {
      const bounds = new maplibregl.LngLatBounds();
      stores.forEach((s) => bounds.extend([s.lng, s.lat]));
      map.fitBounds(bounds, { padding: padding(), maxZoom: 14, duration: map.loaded() ? 900 : 0 });
    }
  }

  // CSS and JS together; the map is drawn once both are in.
  const loadFile = (el) =>
    new Promise((resolve, reject) => {
      el.onload = resolve;
      el.onerror = reject;
      document.head.append(el);
    });

  function loadMapLibre() {
    if (window.maplibregl) return Promise.resolve();
    const css = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: `${MAPLIBRE}.css` });
    const script = Object.assign(document.createElement('script'), { src: `${MAPLIBRE}.js` });
    return Promise.all([loadFile(css), loadFile(script)]);
  }

  function initMap() {
    const bounds = new maplibregl.LngLatBounds();
    stores.forEach((s) => bounds.extend([s.lng, s.lat]));
    map = new maplibregl.Map({
      container: mapBox,
      style: STYLE,
      bounds,
      fitBoundsOptions: { padding: padding(), maxZoom: 14 },
      attributionControl: { compact: true },
      cooperativeGestures: mobile.matches,
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    stores.forEach((store) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'nadine-locator__pin';
      el.innerHTML = PIN;
      el.setAttribute('aria-label', `Nadine Milano ${store.name}`);
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        show(store.key, { push: true, focus: true, via: 'map' });
      });
      el.addEventListener('mouseenter', () => root.querySelector(`.nadine-locator__card[data-store="${store.key}"]`)?.classList.add('is-hovered'));
      el.addEventListener('mouseleave', () => root.querySelector(`.nadine-locator__card[data-store="${store.key}"]`)?.classList.remove('is-hovered'));
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat([store.lng, store.lat]).addTo(map);
      markers.set(store.key, { el, marker });
    });

    map.once('load', () => {
      // The compact credits open by themselves on load; keep them folded behind the (i) button. Zoom buttons sit
      // top-right, away from them: in the same corner the credits' opening and folding moved them (CLS ~0.09).
      mapBox.querySelector('.maplibregl-ctrl-attrib')?.classList.remove('maplibregl-compact-show');
      updateMap();
    });
  }

  // Straight away (the script is deferred): the LCP is the panel's text, so the map does not compete with it.
  loadMapLibre()
    .then(initMap)
    .catch(() => mapBox.classList.add('is-unavailable'));
})();
